import { staticFormApiConnectorLegacy } from 'apiConnector/form/static';
import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import type { StaticFormResult } from 'models/forms/StaticFormResult';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import { commonFormActions } from 'state/forms/actions/form';
import type { StaticProductView } from 'state/product-description/types';
import type { EpicWithThunk } from 'state/state.common';
import { staticFormEnrichmentReceivedThunk } from 'state/static/staticForm.thunks';

export interface StaticProductCodesRequest {
  tabId?: string;
  negotiationId?: string;
  productView?: StaticProductView;
}

export interface StaticRequestProductCodesEpicApi {
  generateProductCodes(request: StaticProductCodesRequest): Observable<StaticFormResult>;
}

export function requestProductCodesEpic(timeoutInMillis: number = 60000, appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { oldHttpClient, frontLogger, selectors: { getStaticFormInfos } }) =>
    action$.pipe(
      ofType('PRODUCT_CODES_REQUESTED'),
      filter(action => action.workspace === 'static'),
      withLatestFrom(state$),
      mergeMap(([{ tabId }, state]) => {
        const { negotiationId } = getStaticFormInfos(state, tabId)!;

        const request: StaticProductCodesRequest = {
          tabId,
          negotiationId,
        };

        const tabRemoved$ = tabRemoved(action$, tabId, state$);

        return staticFormApiConnectorLegacy(oldHttpClient)
          .generateProductCodes(request)
          .pipe(
            takeUntil(tabRemoved$),
            map(
              ({
                values,
                blocksMetadata,
                fieldsMetadata,
                version: newVersion,
                actions,
                quoteQuery,
                errors,
                productView: newProductView,
                mustForceAdvancedMode,
                allowedRequests,
              }) =>
                staticFormEnrichmentReceivedThunk(
                  tabId,
                  newProductView,
                  blocksMetadata,
                  fieldsMetadata,
                  values,
                  newVersion,
                  actions,
                  quoteQuery,
                  mustForceAdvancedMode,
                  allowedRequests,
                  errors,
                ),
            ),
            timeoutWith(timeoutInMillis, throwError(new Error('timeout')), appScheduler),
            catchError(error => {
              if (apiErrorHelper.isHttpError(error) && error.status === 400) {
                return of(commonFormActions.productCodesRejected('static', tabId, error.response.rejections));
              }

              frontLogger.error(
                'Enrichment failure detected on static screen. Session linked to negotiation id {negotiationId} will be rollbacked {@FrontError}',
                negotiationId,
                error,
              );

              return of(commonFormActions.fetchFormEnrichmentFailed('static', tabId, error));
            }),
          );
      }),
    );
}
